import { createSelector } from '@ngrx/store';
import {
  ConversationSummary,
  GENERAL_THREAD_ID,
  PastSession,
  createConversation,
  kpiThreadId,
} from '../models/chatbot.model';
import { getAlioState } from '../reducers';

export interface PastSessionGroup {
  label: string;
  sessions: PastSession[];
}

const dayLabel = (iso: string): string => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  if (sameDay(date, today)) return 'Today';
  if (sameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export const getChatbotState = createSelector(getAlioState, (state) => state.chatbot);

export const getConversations = createSelector(getChatbotState, ({ conversations }) => conversations);

export const getActiveThreadId = createSelector(getChatbotState, ({ activeThreadId }) => activeThreadId);

export const getMessage = createSelector(getChatbotState, ({ message }) => message);

export const getLoadingSessions = createSelector(getChatbotState, ({ loadingSessions }) => loadingSessions);

export const getPastSessions = createSelector(getChatbotState, ({ pastSessions }) => pastSessions);

export const getUsage = createSelector(getChatbotState, ({ usage }) => usage);

export const getActiveConversation = createSelector(
  getConversations,
  getActiveThreadId,
  (conversations, activeThreadId) => conversations[activeThreadId] ?? createConversation(activeThreadId, ''),
);

export const getConversationList = createSelector(
  getConversations,
  getActiveThreadId,
  (conversations, activeThreadId): ConversationSummary[] =>
    Object.values(conversations)
      .map((conversation) => {
        const lastMessage = conversation.messages[conversation.messages.length - 1];
        return {
          threadId: conversation.threadId,
          title: conversation.title,
          preview: lastMessage?.text ?? '',
          active: conversation.threadId === activeThreadId,
        };
      })
      .sort((a, b) => {
        if (a.threadId === GENERAL_THREAD_ID) return -1;
        if (b.threadId === GENERAL_THREAD_ID) return 1;
        return 0;
      }),
);

export const getSessionId = createSelector(getActiveConversation, ({ sessionId }) => sessionId);

export const getMessages = createSelector(getActiveConversation, ({ messages }) => messages);

export const getDisableChat = createSelector(getActiveConversation, ({ disableChat }) => disableChat);

export const getShowIntro = createSelector(getActiveConversation, ({ showIntro }) => showIntro);

export const getShowError = createSelector(getActiveConversation, ({ showError }) => showError);

export const getLoadingHistory = createSelector(getActiveConversation, ({ loadingHistory }) => loadingHistory);

export const getSessionIdForKpi = (kpiId: string) =>
  createSelector(getConversations, (conversations) => conversations[kpiThreadId(kpiId)]?.sessionId ?? '');

export const getPastSessionGroups = createSelector(
  getPastSessions,
  getConversations,
  (pastSessions, conversations): PastSessionGroup[] => {
    const liveSessionIds = new Set(
      Object.values(conversations)
        .map((conversation) => conversation.sessionId)
        .filter((id) => !!id),
    );

    const groups: PastSessionGroup[] = [];
    for (const session of pastSessions) {
      if (liveSessionIds.has(session.id)) continue;
      const label = dayLabel(session.createdAt);
      const group = groups.find((g) => g.label === label);
      if (group) {
        group.sessions.push(session);
      } else {
        groups.push({ label, sessions: [session] });
      }
    }
    return groups;
  },
);
